import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  FileText,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Loader2,
  XCircle,
  Download,
  Euro,
  Clock,
  HandCoins,
  Music,
  UtensilsCrossed,
} from 'lucide-react';
import {
  urlPdfDevis,
  urlPdfFacture,
  fetchBureauTableauDeBord,
  fetchBureauSouscriptions,
  fetchBureauFactures,
  marquerFacturePayee,
  annulerSouscription,
  facturerBureauDevis,
  fetchBureauDons,
  marquerDonPaye,
  createBureauDon,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import CarteExercice from '../components/CarteExercice';

import { dateFr } from '../utils/dates';
const statutLabels: Record<string, string> = {
  brouillon: 'Brouillon',
  soumise: 'Soumise',
  devis_en_attente: 'Devis en attente',
  devis_valide: 'Devis validé',
  devis_refuse: 'Devis refusé',
  facture_emise: 'Facture émise',
  facture_payee: 'Payée',
  facture_en_retard: 'En retard',
  annulee: 'Annulée',
  en_attente_paiement: 'En attente',
  payee: 'Payée',
  impayee: 'Impayée',
};

const statutColors: Record<string, string> = {
  brouillon: 'bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-700',
  soumise: 'bg-blue-50 text-blue-700',
  devis_en_attente: 'bg-amber-50 text-amber-700',
  devis_valide: 'bg-green-50 text-green-700',
  devis_refuse: 'bg-red-50 text-red-700',
  facture_emise: 'bg-purple-50 text-purple-700',
  facture_payee: 'bg-green-50 text-green-700',
  facture_en_retard: 'bg-red-100 text-red-800',
  annulee: 'bg-gray-100 text-gray-500 dark:text-gray-400 dark:bg-gray-700',
  en_attente_paiement: 'bg-amber-50 text-amber-700',
  payee: 'bg-green-50 text-green-700',
  impayee: 'bg-red-50 text-red-700',
};

export default function BureauDashboardPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'souscriptions' | 'factures' | 'dons'>('souscriptions');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [factureEnCours, setFactureEnCours] = useState<number | null>(null);
  const [reference, setReference] = useState('');

  // Sur la grille des droits, et non sur le seul role : c'est la fonction
  // au bureau qui decide desormais de l'acces a cet ecran, et le serveur
  // refusera l'appel si elle ne le prevoit pas.
  const isBureau = aLeDroit('voir_tableau_bord');

  const statsQuery = useQuery({
    queryKey: ['bureau-tableau-de-bord'],
    queryFn: fetchBureauTableauDeBord,
    enabled: isBureau,
  });

  const souscriptionsQuery = useQuery({
    queryKey: ['bureau-souscriptions', filtreStatut],
    queryFn: () => fetchBureauSouscriptions(filtreStatut),
    enabled: isBureau && activeTab === 'souscriptions',
  });

  const facturesQuery = useQuery({
    queryKey: ['bureau-factures', filtreStatut],
    queryFn: () => fetchBureauFactures(filtreStatut),
    enabled: isBureau && activeTab === 'factures',
  });

  const donsQuery = useQuery({
    queryKey: ['bureau-dons', filtreStatut],
    queryFn: () => fetchBureauDons(filtreStatut),
    enabled: isBureau && activeTab === 'dons',
  });

  const payerMutation = useMutation({
    mutationFn: ({ id, ref }: { id: number; ref: string }) => marquerFacturePayee(id, { referencePaiement: ref }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bureau-tableau-de-bord'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-factures'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-souscriptions'] });
      setFactureEnCours(null);
      setReference('');
    },
  });

  const annulerMutation = useMutation({
    mutationFn: annulerSouscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bureau-tableau-de-bord'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-souscriptions'] });
    },
  });

  const facturerMutation = useMutation({
    mutationFn: (devisId: number) => facturerBureauDevis(devisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bureau-tableau-de-bord'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-souscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['bureau-factures'] });
    },
  });

  const payerDonMutation = useMutation({
    mutationFn: marquerDonPaye,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bureau-dons'] });
    },
  });

  if (authLoading || statsQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isBureau) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">Cette page est réservée aux membres du bureau.</p>
          </div>
        </div>
      </div>
    );
  }

  const stats = statsQuery.data?.stats;
  const ss = stats?.souscriptions ?? {};
  const ff = stats?.factures ?? {};

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <LayoutDashboard className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Suivi des ventes</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Ce qui rentre : sponsors, devis, factures et dons. Les dépenses ont leur écran,
            le suivi des achats.
          </p>
        </div>
      </div>

      {/* Exercice comptable : chiffre N, comparaison N-1 et cloture. */}
      <CarteExercice />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Souscriptions" value={ss.total ?? 0} />
        <StatCard icon={FileText} label="Devis en attente" value={(ss.devis_en_attente ?? 0) + (ss.soumise ?? 0)} />
        <StatCard icon={CreditCard} label="Factures impayées" value={(ff.en_attente_paiement ?? 0) + (ff.impayee ?? 0)} />
        <StatCard icon={Euro} label="Encaissé" value={`${stats?.montantEncaisse ?? 0} €`} />
        <StatCard
          icon={HandCoins}
          label="Dons"
          value={stats?.dons?.total ?? 0}
          detail={`${stats?.montantDons ?? 0} € reçus`}
        />
        {/* L'intégralité du financier des ventes : la billetterie aussi. */}
        <StatCard icon={UtensilsCrossed} label="Repas" value={`${stats?.montantRepas ?? 0} €`} />
        <StatCard icon={Music} label="Concerts" value={`${stats?.montantConcerts ?? 0} €`} />
      </div>

      {/* Détails stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card lg:p-5 space-y-3">
          <h2 className="font-semibold">Souscriptions par statut</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ss)
              .filter(([k]) => k !== 'total')
              .map(([k, v]: [string, any]) => (
                <button
                  key={k}
                  onClick={() => { setActiveTab('souscriptions'); setFiltreStatut(k); }}
                  className="px-3 py-1 rounded-full text-xs border hover:border-primary-400 transition"
                >
                  {statutLabels[k] ?? k} : <strong>{v}</strong>
                </button>
              ))}
          </div>
        </div>

        <div className="card lg:p-5 space-y-3">
          <h2 className="font-semibold">Factures par statut</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ff)
              .filter(([k]) => k !== 'total')
              .map(([k, v]: [string, any]) => (
                <button
                  key={k}
                  onClick={() => { setActiveTab('factures'); setFiltreStatut(k); }}
                  className="px-3 py-1 rounded-full text-xs border hover:border-primary-400 transition"
                >
                  {statutLabels[k] ?? k} : <strong>{v}</strong>
                </button>
              ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => { setActiveTab('souscriptions'); setFiltreStatut(''); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            activeTab === 'souscriptions' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 dark:text-gray-400'
          }`}
        >
          Souscriptions
        </button>
        <button
          onClick={() => { setActiveTab('factures'); setFiltreStatut(''); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            activeTab === 'factures' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 dark:text-gray-400'
          }`}
        >
          Factures
        </button>
        <button
          onClick={() => { setActiveTab('dons'); setFiltreStatut(''); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            activeTab === 'dons' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 dark:text-gray-400'
          }`}
        >
          Dons
        </button>
      </div>

      <div className="flex items-center gap-2">
        <select
          value={filtreStatut}
          onChange={(e) => setFiltreStatut(e.target.value)}
          className="input text-sm py-1"
        >
          <option value="">Tous les statuts</option>
          {activeTab === 'souscriptions' ? (
            <>
              <option value="devis_en_attente">Devis en attente</option>
              <option value="devis_valide">Devis validé</option>
              <option value="facture_emise">Facture émise</option>
              <option value="facture_payee">Payée</option>
              <option value="annulee">Annulée</option>
            </>
          ) : activeTab === 'factures' ? (
            <>
              <option value="en_attente_paiement">En attente</option>
              <option value="impayee">Impayée</option>
              <option value="payee">Payée</option>
            </>
          ) : (
            <>
              <option value="en_attente">En attente</option>
              <option value="paye">Payé</option>
              <option value="annule">Annulé</option>
            </>
          )}
        </select>
        {filtreStatut && (
          <button
            onClick={() => setFiltreStatut('')}
            className="text-xs text-gray-500 hover:text-primary-600 dark:text-gray-400"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Liste */}
      {activeTab === 'souscriptions' ? (
        <SouscriptionsList
          query={souscriptionsQuery}
          onAnnuler={(id: number) => annulerMutation.mutate(id)}
          isAnnulationPending={annulerMutation.isPending}
          onFacturer={(devisId: number) => facturerMutation.mutate(devisId)}
          isFacturationPending={facturerMutation.isPending}
        />
      ) : activeTab === 'factures' ? (
        <FacturesList
          query={facturesQuery}
          factureEnCours={factureEnCours}
          setFactureEnCours={setFactureEnCours}
          reference={reference}
          setReference={setReference}
          onPayer={({ id, ref }: { id: number; ref: string }) => payerMutation.mutate({ id, ref })}
          isPaiementPending={payerMutation.isPending}
        />
      ) : (
        <div className="space-y-4">
          {/* Le billet glissé à la buvette ne passe par aucun formulaire en
              ligne : le bureau le saisit ici, encaissement compris — un don
              sans paiement n'entrerait dans le chiffre d'aucun exercice. */}
          <FormulaireDonManuel
            onEnregistre={() => {
              queryClient.invalidateQueries({ queryKey: ['bureau-dons'] });
              queryClient.invalidateQueries({ queryKey: ['bureau-tableau-de-bord'] });
              queryClient.invalidateQueries({ queryKey: ['bureau-exercice-chiffres'] });
            }}
          />
          <DonsList
            query={donsQuery}
            onPayer={(id: number) => payerDonMutation.mutate(id)}
            isPaiementPending={payerDonMutation.isPending}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Saisie d'un don reçu en main propre : espèces ou chèque.
 *
 * Le don est enregistré déjà encaissé, à la date choisie — un chèque déposé
 * la semaine dernière se compte la semaine dernière. Anonyme si le donateur
 * n'a pas laissé de nom.
 */
function FormulaireDonManuel({ onEnregistre }: { onEnregistre: () => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [form, setForm] = useState({
    montant: '',
    prenom: '',
    nom: '',
    anonyme: false,
    modePaiement: 'especes',
    datePaiement: new Date().toISOString().slice(0, 10),
  });
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const creer = useMutation({
    mutationFn: () =>
      createBureauDon({
        montant: Number(form.montant),
        modePaiement: form.modePaiement,
        prenom: form.prenom,
        nom: form.nom,
        anonyme: form.anonyme,
        datePaiement: form.datePaiement,
      }),
    onSuccess: (d: any) => {
      setNotice({ type: 'success', message: d.message });
      setOuvert(false);
      setForm({
        montant: '',
        prenom: '',
        nom: '',
        anonyme: false,
        modePaiement: 'especes',
        datePaiement: new Date().toISOString().slice(0, 10),
      });
      onEnregistre();
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {notice ? (
          <p
            className={`text-sm rounded-xl px-3 py-2 flex-1 ${
              notice.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {notice.message}
          </p>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => {
            setOuvert(!ouvert);
            setNotice(null);
          }}
          className="btn-primary inline-flex items-center gap-2"
        >
          <HandCoins className="w-4 h-4" /> Nouveau don
        </button>
      </div>

      {ouvert && (
        <div className="card lg:p-5 space-y-3 border-2 border-primary-200 dark:border-primary-800">
          <p className="font-semibold text-gray-900 dark:text-gray-100">
            Don reçu en main propre
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="relative">
              <input
                className="input pr-8"
                type="number"
                min={1}
                placeholder="Montant *"
                value={form.montant}
                onChange={(e) => setForm({ ...form, montant: e.target.value })}
              />
              <Euro className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <select
              className="input"
              value={form.modePaiement}
              onChange={(e) => setForm({ ...form, modePaiement: e.target.value })}
              aria-label="Mode de paiement"
            >
              <option value="especes">Espèces</option>
              <option value="cheque">Chèque</option>
              <option value="virement">Virement</option>
            </select>
            <label className="text-sm text-gray-600 dark:text-gray-400 block">
              Date d'encaissement
              <input
                className="input mt-1"
                type="date"
                value={form.datePaiement}
                onChange={(e) => setForm({ ...form, datePaiement: e.target.value })}
              />
            </label>
            {!form.anonyme && (
              <>
                <input
                  className="input"
                  placeholder="Prénom"
                  value={form.prenom}
                  onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Nom"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                />
              </>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.anonyme}
                onChange={(e) => setForm({ ...form, anonyme: e.target.checked })}
                className="w-4 h-4"
              />
              Don anonyme
            </label>
          </div>

          <button
            type="button"
            onClick={() => creer.mutate()}
            disabled={creer.isPending || Number(form.montant) <= 0}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            {creer.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            Enregistrer le don encaissé
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: any;
  label: string;
  value: string | number;
  /** Précision sous le libellé — ce que les dons représentent, par exemple. */
  detail?: string;
}) {
  return (
    <div className="card p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        <p className="text-xs text-gray-600 dark:text-gray-400">{label}</p>
        {detail && <p className="text-xs text-primary-700 dark:text-primary-400 font-medium">{detail}</p>}
      </div>
    </div>
  );
}

function SouscriptionsList({
  query,
  onAnnuler,
  isAnnulationPending,
  onFacturer,
  isFacturationPending,
}: any) {
  if (query.isPending) {
    return <Loader2 className="w-6 h-6 animate-spin text-primary-600" />;
  }

  const souscriptions = query.data?.souscriptions ?? [];

  return (
    <div className="space-y-3">
      {souscriptions.length === 0 ? (
        <p className="text-gray-500 text-sm dark:text-gray-400">Aucune souscription trouvée.</p>
      ) : (
        souscriptions.map((s: any) => (
          <div key={s.id} className="card p-4 lg:p-5 space-y-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">{s.sponsor?.nom ?? '—'}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{s.sponsor?.email ?? ''}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statutColors[s.statut] ?? 'bg-gray-100'}`}>
                  {statutLabels[s.statut] ?? s.statut}
                </span>
                <span className="font-bold text-primary-700">{s.montantTotal} €</span>
              </div>
            </div>

            <div className="text-sm text-gray-600 grid grid-cols-1 lg:grid-cols-3 gap-2 dark:text-gray-400">
              <p>Livret : {s.planLivret ?? 'Aucun'}</p>
              <p>Écocup : {s.planEcocup ?? 'Aucun'}</p>
              <p>T-shirt : {s.planTshirt ?? 'Aucun'}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              {s.devis?.pdfUrl && (
                <a
                  href={urlPdfDevis(s.devis.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                >
                  <Download className="w-4 h-4" /> Devis
                </a>
              )}
              {s.devis?.tokenValidation && (
                <a
                  href={`${window.location.origin}/api/devis/valider/${s.devis.tokenValidation}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                >
                  Lien validation devis
                </a>
              )}
              {/* Valider le devis au nom du sponsor : au téléphone, un accord
                  verbal suffit souvent, et attendre le clic sur le lien
                  bloquerait la facturation. */}
              {s.devis && !s.facture && (
                <button
                  onClick={() => onFacturer(s.devis.id as number)}
                  disabled={isFacturationPending}
                  className="inline-flex items-center gap-1 text-green-700 hover:text-green-800"
                >
                  <FileText className="w-4 h-4" /> Valider et facturer
                </button>
              )}
              {s.facture?.pdfUrl && (
                <a
                  href={urlPdfFacture(s.facture.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                >
                  <Download className="w-4 h-4" /> Facture
                </a>
              )}
              {!['payee', 'annulee', 'facture_payee'].includes(s.statut) && (
                <button
                  onClick={() => onAnnuler(s.id as number)}
                  disabled={isAnnulationPending}
                  className="ml-auto inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-sm"
                >
                  <XCircle className="w-4 h-4" /> Annuler
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function FacturesList({ query, factureEnCours, setFactureEnCours, reference, setReference, onPayer, isPaiementPending }: any) {  if (query.isPending) {
    return <Loader2 className="w-6 h-6 animate-spin text-primary-600" />;
  }

  const factures = query.data?.factures ?? [];

  return (
    <div className="space-y-3">
      {factures.length === 0 ? (
        <p className="text-gray-500 text-sm dark:text-gray-400">Aucune facture trouvée.</p>
      ) : (
        factures.map((f: any) => (
          <div key={f.id} className="card p-4 lg:p-5 space-y-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">Facture n° {f.numero}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{f.sponsor?.nom ?? '—'} — {f.sponsor?.email ?? ''}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statutColors[f.statut] ?? 'bg-gray-100'}`}>
                  {statutLabels[f.statut] ?? f.statut}
                </span>
                <span className="font-bold text-primary-700">{f.souscription?.montantTotal ?? 0} €</span>
              </div>
            </div>

            <div className="text-sm text-gray-600 grid grid-cols-1 lg:grid-cols-3 gap-2 dark:text-gray-400">
              <p className="flex items-center gap-1">
                <CreditCard className="w-4 h-4" /> Mode : {f.modePaiement ?? '—'}
              </p>
              <p className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> Échéance : {f.echeanceLe ? dateFr(f.echeanceLe) : '—'}
              </p>
              <p className="flex items-center gap-1">
                Réf. : {f.referencePaiement ?? '—'}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {f.pdfUrl && (
                <a
                  href={urlPdfFacture(f.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-700 hover:underline text-sm"
                >
                  <Download className="w-4 h-4" /> Télécharger
                </a>
              )}

              {f.statut !== 'payee' && (
                <>
                  {factureEnCours === f.id ? (
                    <div className="flex items-center gap-2 ml-auto">
                      <input
                        type="text"
                        placeholder="Référence de paiement"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        className="input text-sm py-1"
                      />
                      <button
                        onClick={() => onPayer({ id: f.id as number, ref: reference })}
                        disabled={isPaiementPending}
                        className="btn-primary px-3 py-1 text-sm inline-flex items-center gap-1"
                      >
                        {isPaiementPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Valider
                      </button>
                      <button
                        onClick={() => { setFactureEnCours(null); setReference(''); }}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
                      >
                        Annuler
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setFactureEnCours(f.id)}
                      className="ml-auto inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 font-medium"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Marquer payée
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const donModeLabels: Record<string, string> = {
  carte: 'Carte (Stripe)',
  crypto: 'Bitcoin',
};

const donStatutLabels: Record<string, string> = {
  en_attente: 'En attente',
  paye: 'Payé',
  annule: 'Annulé',
};

const donStatutColors: Record<string, string> = {
  en_attente: 'bg-amber-50 text-amber-700',
  paye: 'bg-green-50 text-green-700',
  annule: 'bg-gray-100 text-gray-500 dark:text-gray-400 dark:bg-gray-700',
};

function DonsList({ query, onPayer, isPaiementPending }: any) {
  if (query.isPending) {
    return <Loader2 className="w-6 h-6 animate-spin text-primary-600" />;
  }

  const dons = query.data?.dons ?? [];

  return (
    <div className="space-y-3">
      {dons.length === 0 ? (
        <p className="text-gray-500 text-sm dark:text-gray-400">Aucun don trouvé.</p>
      ) : (
        dons.map((d: any) => (
          <div key={d.id} className="card p-4 lg:p-5 space-y-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {d.anonyme ? 'Don anonyme' : [d.prenom, d.nom].filter(Boolean).join(' ') || 'Donateur'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {d.createdAt ? dateFr(d.createdAt) : '—'} · {donModeLabels[d.modePaiement] ?? d.modePaiement}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${donStatutColors[d.statut] ?? 'bg-gray-100'}`}>
                  {donStatutLabels[d.statut] ?? d.statut}
                </span>
                <span className="font-bold text-primary-700">{d.montant} €</span>
              </div>
            </div>

            {d.statut !== 'paye' && (
              <div className="flex justify-end">
                <button
                  onClick={() => onPayer(d.id as number)}
                  disabled={isPaiementPending}
                  className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 font-medium"
                >
                  {isPaiementPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Marquer payé
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
